import { t as __t } from "i18next";
import React, { useState, useEffect, useMemo } from "react";
import { notifyListRefresh } from "../../../utils/listNavigation";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
	callGetGrandUserPerssionById,
	callGetSubMenu,
	callGrandPermission
} from "../../../api/settings/service";
import CopyrightFooter from "../../../components/footer/employers/CopyrightFooter";
import DashboardHeader from "../../../components/header/DashboardHeader";
import BackPrevious from "../../../components/back-previous";
import UserAutoComplete from "../../../components/user-auto-complete";
import DashboardEmployerSidebar from "../../../components/header/DashboardSidebar";
import MobileMenu from "../../../components/header/MobileMenu";
import { toast } from "react-toastify";
import "../../../components/i18n/lang";
import { decodeBase64, handleGetCookie, handleInput } from "../../../utils/commonUtil";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import colors from "../../../utils/colors";
import "../../style.css";

const permissionLabelKeys = {
	0: "permission.actions.claim",
	1: "permission.actions.create",
	2: "permission.actions.edit",
	7: "permission.actions.print",
	4: "permission.actions.delete",
	5: "permission.actions.approvePay",
	6: "permission.actions.rejectCancel",
	8: "permission.actions.paymentForFee",
};

const buildPermissionGroups = (items = []) => {
	const parents = items
		.filter((item) => item.mainMenuId === null)
		.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));

	return parents.map((parent) => {
		const children = items.filter((item) => item.mainMenuId === parent.id);
		const isPage = (item) => item.permisionControlId === null || item.permisionControlId === undefined;
		const actionItems = children.filter((item) => !isPage(item));
		const usedActionIds = new Set();

		let pages = children.filter(isPage).map((item) => {
			const actions = actionItems.filter((action) =>
				action.name === item.name && (action.routeUrl || "") === (item.routeUrl || "")
			);
			actions.forEach((action) => usedActionIds.add(action.id));
			return { ...item, actions };
		});

		const directActions = actionItems.filter((action) =>
			!usedActionIds.has(action.id) &&
			action.name === parent.name &&
			(action.routeUrl || "") === (parent.routeUrl || "")
		);
		directActions.forEach((action) => usedActionIds.add(action.id));

		if (parent.routeUrl && (directActions.length > 0 || pages.length === 0)) {
			pages.unshift({
				id: parent.id,
				name: parent.name,
				description: parent.description,
				routeUrl: parent.routeUrl,
				icons: parent.icons,
				priority: parent.priority,
				actions: directActions,
				isParentPage: true,
			});
		}

		actionItems.filter((action) => !usedActionIds.has(action.id)).forEach((action) => {
			pages.push({
				id: action.id,
				name: action.name,
				description: action.description,
				routeUrl: action.routeUrl,
				icons: action.icons,
				priority: action.priority,
				actions: [action],
			});
		});

		pages = pages.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
		return { ...parent, pages };
	});
};

const Index = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const params = useParams();

	const [selectedSubMenus, setSelectedSubMenus] = useState([]);
	const [subMenu, setSubMenu] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [activePageId, setActivePageId] = useState(null);

	const [userPermis, setUserPermission] = useState({
		id: null,
		userId: "",
		firstName: "",
		lastName: "",
		commissionRate: "",
		carriredName: "",
		carriredCode: "",
		documentPath: null,
		isFocus: false,
		mainMenuId: "",
		isSelectedAll: false,
		rolesCode: ""
	});

	useEffect(() => {
		if (Object.keys(params).length > 0) {
			const id = decodeBase64(params?.id);
			if (id != null) {
				getUserPermissionById(id);
			}
		}

		const authData = handleGetCookie("auth");
		if (authData?.user?.userId) {
			setUserPermission((prev) => ({
				...prev,
				carriredCode: authData?.user?.userId,
				carriredName: `${authData?.user?.firstName || ""} ${authData?.user?.lastName || ""}`,
			}));
		}

		loadSubMenu(null);
	}, []);

	const getUserPermissionById = async (id) => {
		try {
			const rsp = await dispatch(callGetGrandUserPerssionById({ userId: id }));
			if (rsp?.payload?.responseCode === "00000") {
				const item = rsp?.payload?.data?.user;

				setUserPermission((prev) => ({
					...prev,
					id: item?.id || null,
					userId: item?.userId || "",
					firstName: item?.firstName || "",
					lastName: item?.lastName || "",
					rolesCode: item?.rolesCode || "",
				}));

				const roles = rsp?.payload?.data?.permission;
				if (roles != null) {
					setSelectedSubMenus(roles.map(item => item.webControlId));
				}
			}
		} catch (error) {
			console.error(error);
		}
	};

	const loadSubMenu = async (id) => {
		const req = {
			menuId: id,
			page: 1,
			maxResult: 1000
		};

		const rsp = await dispatch(callGetSubMenu(req));
		console.log("subMenu", rsp);

		if (rsp?.payload?.responseCode === "00000") {
			const items = rsp?.payload?.data || [];
			setSubMenu(items);
			const firstPage = buildPermissionGroups(items)
				.flatMap((group) => group.pages)[0];
			if (firstPage) setActivePageId((current) => current ?? firstPage.id);
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setUserPermission({
			...userPermis,
			[name]: value,
		});
	};

	const isChecked = (id) => selectedSubMenus.includes(id);

	const groups = useMemo(() => buildPermissionGroups(subMenu), [subMenu]);

	const allSelectableIds = useMemo(
		() => groups.flatMap((group) => [
			group.id,
			...group.pages.flatMap((page) => [page.id, ...page.actions.map((action) => action.id)])
		]),
		[groups]
	);

	const handleSelectedAll = () => {
		const allIds = [...new Set(allSelectableIds)];
		const isAllSelected = allIds.every(id => selectedSubMenus.includes(id));

		if (isAllSelected) {
			setSelectedSubMenus([]);
		} else {
			setSelectedSubMenus(allIds);
		}
	};

	const toggleGroup = (group) => {
		const ids = [
			group.id,
			...group.pages.flatMap(page => [
				page.id,
				...page.actions.map(action => action.id)
			])
		];

		const isAll = ids.every(id => selectedSubMenus.includes(id));

		setSelectedSubMenus(prev => {
			if (isAll) {
				return prev.filter(id => !ids.includes(id));
			}

			return [...new Set([...prev, ...ids])];
		});
	};

	const togglePage = (group, page) => {
		const pageIds = [page.id, ...page.actions.map((action) => action.id)];
		const isEnabled = selectedSubMenus.includes(page.id);

		setSelectedSubMenus((current) => {
			if (isEnabled) {
				const next = current.filter((id) => !pageIds.includes(id));
				const hasAnotherSelectedPage = group.pages.some(
					(item) => item.id !== page.id && next.includes(item.id)
				);
				return hasAnotherSelectedPage ? next : next.filter((id) => id !== group.id);
			}

			return [...new Set([...current, group.id, page.id])];
		});
	};

	const toggleAction = (group, page, action) => {
		setSelectedSubMenus((current) => {
			if (current.includes(action.id)) {
				return current.filter((id) => id !== action.id);
			}

			return [...new Set([...current, group.id, page.id, action.id])];
		});
	};

	const getGroupSelectedCount = (group) => {
		const ids = [
			group.id,
			...group.pages.flatMap(page => [
				page.id,
				...page.actions.map(action => action.id)
			])
		];

		return ids.filter(id => selectedSubMenus.includes(id)).length;
	};

	const getFilteredGroups = () => {
		if (!searchText.trim()) return groups;

		const keyword = searchText.toLowerCase();

		return groups
			.map(group => {
				const groupMatch =
					group.description?.toLowerCase().includes(keyword) ||
					group.name?.toLowerCase().includes(keyword) ||
					group.groupName?.toLowerCase().includes(keyword);

				const pages = group.pages.filter(page => {
					const pageMatch =
						page.description?.toLowerCase().includes(keyword) ||
						page.name?.toLowerCase().includes(keyword) ||
						page.routeUrl?.toLowerCase().includes(keyword);

					const actionMatch = page.actions.some(action =>
						action.description?.toLowerCase().includes(keyword) ||
						action.name?.toLowerCase().includes(keyword) ||
						action.routeUrl?.toLowerCase().includes(keyword)
					);

					return pageMatch || actionMatch;
				});

				if (groupMatch) return group;

				return {
					...group,
					pages
				};
			})
			.filter(group => group.pages.length > 0);
	};

	const handleSave = async (e) => {
		e.preventDefault();

		if (!userPermis.userId) {
			toast.error(__t("uiText.text364"), { position: "top-left" });
			return;
		}

		if (selectedSubMenus.length === 0) {
			toast.error(t("settings.userPermission.pleaseSelectMenu"), { position: "top-left" });
			return;
		}

		Swal.fire({
			title: t("titleSave"),
			text: t("confirmSave"),
			icon: "warning",
			reverseButtons: true,
			showCancelButton: true,
			confirmButtonColor: colors.confirmMessage,
			cancelButtonColor: colors.cancel,
			confirmButtonText: t("confirm"),
			cancelButtonText: t("cancel")
		}).then(async (result) => {
			if (result.isConfirmed) {
				try {
					const req = {
						userId: userPermis.userId,
						carriredCode: userPermis.carriredCode,
						carriredName: userPermis.carriredName,
						roleMenu: selectedSubMenus
					};

					const res = await dispatch(callGrandPermission(req));

					if (res?.payload?.responseCode === "00000") {
						Swal.fire({
							icon: "success",
							title: t("success.add"),
							confirmButtonColor: colors.confirmMessage,
							confirmButtonText: t("close"),
						}).then(() => {
							notifyListRefresh();
							navigate(-1);
						});
					} else {
						toast.error(
							`${res?.payload?.responseCode} - ${res?.payload?.responseDescription}`,
							{ position: "top-left" }
						);
					}
				} catch (error) {
					Swal.fire(__t("uiText.text023"), __t("uiText.text024"), "error");
				}
			}
		});
	};

	const renderSetup = () => {
		const filteredGroups = getFilteredGroups();
		const isAllSelected = allSelectableIds.length > 0 &&
			allSelectableIds.every((id) => selectedSubMenus.includes(id));
		const activeGroup = groups.find((group) =>
			group.pages.some((page) => page.id === activePageId)
		);
		const activePage = activeGroup?.pages.find((page) => page.id === activePageId);
		const activePageEnabled = activePage ? isChecked(activePage.id) : false;

		return (
			<div className="permission-page mt-3">
				<div className="permission-header">
					<div>
						<h5 className="mb-1">
							<i className="la la-shield-alt me-1"></i>
							{t("settings.userPermission.grandPermission")}
						</h5>
						<p className="text-muted mb-0">
							{__t("uiText.text365")}</p>
					</div>

					<div className="permission-summary">
						<span>{selectedSubMenus.length}</span>
						<small>{__t("uiText.text366")}</small>
					</div>
				</div>

				<div className="permission-toolbar">
					<div className="permission-search">
						<i className="la la-search"></i>
						<input
							type="text"
							placeholder={t("settings.userPermission.searchPlaceholder")}
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
						/>
					</div>

					<button
						type="button"
						className="btn btn-sm btn-outline-primary"
						onClick={handleSelectedAll}
					>
						<i className="la la-check-circle"></i>{" "}
						{isAllSelected
							? t("settings.userPermission.disableAll")
							: t("settings.userPermission.enableAll")}
					</button>
				</div>

				{filteredGroups.length > 0 ? (
					<div className="permission-simulator">
						<aside className="permission-site-map">
							<div className="permission-site-map-head">
								<span className="permission-logo-mark">
									<i className="la la-shield-alt" />
								</span>
								<div>
									<strong>{t("settings.userPermission.siteMap")}</strong>
									<small>{t("settings.userPermission.siteMapHint")}</small>
								</div>
							</div>

							<div className="permission-site-groups">
								{filteredGroups.map((group) => {
									const selectedCount = getGroupSelectedCount(group);
									const groupIds = [
										group.id,
										...group.pages.flatMap((page) => [
											page.id,
											...page.actions.map((action) => action.id),
										]),
									];
									const isGroupEnabled = groupIds.every((id) => isChecked(id));

									return (
										<section className="permission-site-group" key={group.id}>
											<div className="permission-site-group-title">
												<label className="permission-check-control">
													<input
														type="checkbox"
														checked={isGroupEnabled}
														onChange={() => toggleGroup(group)}
													/>
													<span className="permission-custom-check" />
												</label>
												<span className="permission-group-icon">
													<i className={`la ${group.groupIcons || "la-folder"}`} />
												</span>
												<strong>{t(group.groupName || group.name)}</strong>
												<span className="permission-group-count">{selectedCount}</span>
											</div>
											<div className="permission-site-pages">
												{group.pages.map((page) => {
													const enabled = isChecked(page.id);
													const selectedActions = page.actions.filter((action) => isChecked(action.id)).length;
													const isPreviewed = page.id === activePageId;

													return (
														<div
															className={`permission-site-page ${isPreviewed ? "active" : ""} ${enabled ? "enabled" : ""}`}
															key={page.id}
														>
															<label className="permission-check-control" title={t("settings.userPermission.enablePage")}>
																<input
																	type="checkbox"
																	checked={enabled}
																	onChange={() => togglePage(group, page)}
																/>
																<span className="permission-custom-check" />
															</label>

															<button
																type="button"
																className="permission-page-preview-button"
																onClick={() => setActivePageId(page.id)}
															>
																<span>
																	<strong>{t(page.name)}</strong>
																	<small>
																		{page.actions.length > 0
																			? `${selectedActions}/${page.actions.length} ${t("settings.userPermission.buttons")}`
																			: t("settings.userPermission.noButtonsShort")}
																	</small>
																</span>
																<span className="permission-view-label">
																	<i className="la la-eye" />
																	{t("settings.userPermission.viewButtons")}
																</span>
															</button>
														</div>
													);
												})}
											</div>
										</section>
									);
								})}
							</div>
						</aside>

						<main className="permission-browser">
							<div className="permission-browser-bar">
								<div className="permission-browser-dots"><i /><i /><i /></div>
								<div className="permission-browser-address">
									<i className="la la-lock" />
									<span>{activePage?.routeUrl || "/admin"}</span>
								</div>
							</div>

							{activePage && activeGroup ? (
								<div className="permission-browser-body">
									<div className="permission-preview-header">
										<div>
											<span className="permission-preview-eyebrow">{t(activeGroup.groupName || activeGroup.name)}</span>
											<h4>{t(activePage.name)}</h4>
											<p>{activePage.description || t("settings.userPermission.previewDescription")}</p>
										</div>
										<button
											type="button"
											className={`permission-page-state ${activePageEnabled ? "enabled" : ""}`}
											onClick={() => togglePage(activeGroup, activePage)}
										>
											<i className={`la ${activePageEnabled ? "la-toggle-on" : "la-toggle-off"}`} />
											{activePageEnabled
												? t("settings.userPermission.menuEnabled")
												: t("settings.userPermission.menuDisabled")}
										</button>
									</div>

									<section className="permission-button-panel">
										<div className="permission-button-panel-head">
											<div>
												<h6><i className="la la-mouse-pointer" /> {t("settings.userPermission.buttonsTitle")}</h6>
												<p>{t("settings.userPermission.buttonsHint")}</p>
											</div>
											<span>{activePage.actions.filter((action) => isChecked(action.id)).length}/{activePage.actions.length}</span>
										</div>

										{activePage.actions.length > 0 ? (
											<div className="permission-preview-actions">
												{activePage.actions.map((action) => {
													const enabled = isChecked(action.id);
													const label = permissionLabelKeys[action.permisionControlId]
														? t(permissionLabelKeys[action.permisionControlId])
														: action.description;

													return (
														<button
															type="button"
															key={action.id}
															className={`permission-preview-action ${enabled ? "enabled" : ""}`}
															onClick={() => toggleAction(activeGroup, activePage, action)}
															aria-pressed={enabled}
														>
															<span className="permission-action-check">
																<i className={`la ${enabled ? "la-check" : "la-plus"}`} />
															</span>
															<span>
																<strong>{label}</strong>
																<small>{enabled
																	? t("settings.userPermission.buttonEnabled")
																	: t("settings.userPermission.buttonDisabled")}</small>
															</span>
														</button>
													);
												})}
											</div>
										) : (
											<div className="permission-no-actions">
												<i className="la la-info-circle" />
												<div>
													<strong>{t("settings.userPermission.noButtons")}</strong>
													<p>{t("settings.userPermission.noButtonsHint")}</p>
												</div>
											</div>
										)}
									</section>

									<div className="permission-hidden-content" aria-hidden="true">
										<div className="permission-skeleton-row"><i /><i /><i /></div>
										<div className="permission-skeleton-table">
											<i /><i /><i /><i />
										</div>
										<div className="permission-hidden-message">
											<span><i className="la la-eye-slash" /></span>
											<strong>{t("settings.userPermission.contentHidden")}</strong>
											<small>{t("settings.userPermission.contentHiddenHint")}</small>
										</div>
									</div>
								</div>
							) : (
								<div className="permission-browser-empty">
									<i className="la la-hand-pointer" />
									<strong>{t("settings.userPermission.selectPage")}</strong>
								</div>
							)}
						</main>
					</div>
				) : (
					<div className="text-center p-4">{t("nodata")}</div>
				)}
			</div>
		);
	};

	return (
		<div className="page-wrapper dashboard">
			<span className="header-span"></span>

			<DashboardHeader />
			<MobileMenu />
			<DashboardEmployerSidebar />

			<section className="user-dashboard">
				<div className="dashboard-outer">
					<div className="upper-title-box back-title-group">
						<BackPrevious />
						<h4>
							{userPermis.id === null
								? t("settings.userPermission.add")
								: t("settings.userPermission.edit")}
						</h4>
					</div>

					<form onSubmit={handleSave}>
						<div className="ls-widget">
							<div className="tabs-box">
								<div className="widget-content p-4">
									<div className="default-form">
										<div className="row">
											<div className="col-md-3 form-group">
												<label>
													{t("user.id")} <span className="text-danger">*</span>
												</label>

												<UserAutoComplete
													value={userPermis.userId}
													initialData={userPermis.userId}
													onSelect={(selected) => {
														setUserPermission((prev) => ({
															...prev,
															userId: selected.userId,
															firstName: selected.firstName,
															lastName: selected.lastName,
															rolesCode: selected.rolesCode,
															isFocus: true
														}));
													}}
												/>
											</div>

											<div className="col-md-3 form-group">
												<label>
													{t("user.firstName")} <span className="text-danger">*</span>
												</label>

												<input
													type="text"
													name="firstName"
													className="form-control-uld"
													placeholder={t("user.firstName")}
													onInput={handleInput}
													onChange={handleChange}
													value={userPermis.firstName}
													autoComplete="off"
													required
													readOnly
												/>
											</div>

											<div className="col-md-3 form-group">
												<label>{t("user.lastName")}</label>

												<input
													type="text"
													name="lastName"
													className="form-control-uld"
													placeholder={t("user.lastName")}
													onInput={handleInput}
													onChange={handleChange}
													value={userPermis.lastName}
													readOnly
													required
													autoComplete="off"
												/>
											</div>

											<div className="col-md-3 form-group">
												<label>{t("user.role")}</label>

												<input
													type="text"
													name="rolesCode"
													className="form-control-uld"
													placeholder={t("user.role")}
													onInput={handleInput}
													onChange={handleChange}
													value={userPermis.rolesCode}
													readOnly
													autoComplete="off"
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="ls-widget">
							<div className="tabs-box">
								<div className="widget-content p-4">
									<div className="default-form">
										{renderSetup()}
									</div>
								</div>
							</div>
						</div>

						<div className="permission-save-bar">
							<div>
								<strong>{selectedSubMenus.length}</strong> {__t("uiText.text366")}</div>

							<button className="theme-btn btn-style-one" type="submit">
								<i className="la la-save"></i>&nbsp;
								{t("button.save")}
							</button>
						</div>
					</form>
				</div>
			</section>

			<CopyrightFooter />
		</div>
	);
};

export default Index;
